import {Stack} from '@mui/material';
import moment, {Moment} from 'moment';
import SaveIcon from '@mui/icons-material/SaveAlt';
import {useTranslation} from 'react-i18next';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import GenericHeader from '../../../generic-header/generic-header';
import {DateRangeInput} from '../../../date-range-input/date-range-input';
import {DateRangeOnChange} from '../../../date-range-input/date-range-utils';
import {CareServiceAddButton} from './care-service-add-button';
import {FetchEntityUsersResult} from '../../../../hooks/use-fetch-entity-users';
import {createNewCareServiceItem} from '../data/care-service-data-transformation';
import {CareServiceCreatingItem} from '../data/care-service-item';
import {CareServicesUserInput} from './care-services-user-input';
import {translations} from '../../../../i18n/translations';

export type CareServicesHeaderProps = {
  startDate: Moment;
  endDate: Moment;
  canWrite?: boolean;
  crumbs?: {label?: string; link: string}[];
  isLoading?: boolean;
  filterUsers?: FetchEntityUsersResult;
  filterDefaultOptionLabel?: string;
  onDateChange?: DateRangeOnChange;
  onCreateItemClick?: (newItem: CareServiceCreatingItem) => void;
  onFilterUserChange?: (user: AffiliatedUser | undefined) => void;
  onExportPdfClick?: () => void;
  onExportXlsxClick?: () => void;
};

export function CareServicesHeader(props: CareServicesHeaderProps) {
  const {t} = useTranslation();

  return (
    <GenericHeader
      isLoading={props.isLoading ?? false}
      titleCrumbs={props.crumbs}
      leftColumn={
        // * --- USER FILTER --- */
        props.filterUsers ? (
          <Stack direction="row" alignItems="center" gap="10px">
            <CareServicesUserInput
              usersData={props.filterUsers}
              onChange={props.onFilterUserChange}
              defaultOptionLabel={props.filterDefaultOptionLabel}
            />
          </Stack>
        ) : (
          <></>
        )
      }
      rightColumn={
        <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
          {/* --- DATE INPUT --- */}
          <DateRangeInput
            mode="strict"
            startDate={props.startDate}
            endDate={props.endDate}
            onChange={props.onDateChange}
          />
          {/* --- ADD INPUT --- */}
          <CareServiceAddButton
            disabled={!props.canWrite}
            onClick={() => {
              const start = moment().minute(0);
              const end = moment(start).add(1, 'hour');
              const item = createNewCareServiceItem({start, end});
              props.onCreateItemClick?.(item);
            }}
          />
        </Stack>
      }
      actions={
        props.onExportPdfClick || props.onExportXlsxClick
          ? {
              actions: [
                ...(props.onExportPdfClick
                  ? [
                      {
                        label: t(translations.office.generics.save_pdf),
                        icon: {element: SaveIcon},
                        callback: () => props.onExportPdfClick?.(),
                      },
                    ]
                  : []),
                ...(props.onExportXlsxClick
                  ? [
                      {
                        label: t(translations.office.generics.save_xlsx),
                        icon: {element: SaveIcon},
                        callback: () => props.onExportXlsxClick?.(),
                      },
                    ]
                  : []),
              ],
            }
          : undefined
      }
    />
  );
}
